<template>
  <component :is="layoutComponent">
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">Clients ({{ totalRows }})</h1>
          <small class="text-muted">
            {{ clientsPageDescription }}
          </small>
        </div>

        <div class="d-flex gap-2">
          <ModuleActivityButton :module="['clients', 'Client']" label="Clients" />

          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            :class="{ active: filtersVisible }"
            :title="filtersVisible ? 'Masquer les filtres' : 'Afficher les filtres'"
            @click="toggleFilters"
          >
            <i class="fa" :class="filtersVisible ? 'fa-eye-slash' : 'fa-filter'"></i>
            <span class="btn-label ms-1">Filtres</span>
          </button>

          <button
            v-if="canManageClients"
            class="btn btn-primary btn-sm btn-icon-label"
            type="button"
            @click="openCreateDialog"
          >
            <i class="fa fa-plus"></i>
            <span class="btn-label ms-1">Nouveau client</span>
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger mb-3 py-2">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="alert alert-success mb-3 py-2">
        {{ successMessage }}
      </div>

      <!-- Filtre -->
      <div v-show="filtersVisible" class="card module-filter-card mb-3">
        <div class="card-body py-2">
          <div class="row g-2 align-items-end">
            <div class="col-md-3">
              <label class="form-label small mb-1">Type</label>
              <PrimevueDropdown
                v-model="filters.type"
                :options="typeFilterOptions"
                optionLabel="label"
                optionValue="value"
                class="w-100"
              />
            </div>

            <div class="col-12 module-filter-actions mt-2">
              <button type="button" class="btn btn-outline-secondary btn-sm btn-icon-label" @click="resetFilters">
                <i class="fa fa-rotate-left"></i>
                <span class="btn-label ms-1">Réinitialiser</span>
              </button>
              <button type="button" class="btn btn-primary btn-sm btn-icon-label" @click="fetchClients">
                <i class="fa fa-search"></i>
                <span class="btn-label ms-1">Filtrer</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <BootstrapDataTable
        :response="clientsResponse"
        :columns="tableColumns"
        :loading="isLoading"
        :sort-by="table.sortBy"
        :sort-dir="table.sortDir"
        :per-page="table.perPage"
        :active-filter-count="activeFilterCount"
        state-key="clients.list"
        searchable
        v-model:search-value="filters.search"
        search-placeholder="Rechercher nom, téléphone, WhatsApp, email..."
        detail-route-name="ClientShow"
        empty-text="Aucun client trouvé."
        @search-change="handleTableSearch"
        @sort-change="handleSortChange"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
        @state-restored="Object.assign(table, { page: $event.page, perPage: $event.perPage })"
        @refresh="fetchClients"
      >
        <template #cell-total_sales_amount="{ row }">
          {{ $format.currency(row.total_sales_amount || 0) }}
        </template>
        <template #cell-current_debt="{ row }">
          <span class="fw-semibold" :class="Number(row.current_debt || 0) > 0 ? 'text-danger' : ''">
            {{ $format.currency(row.current_debt || 0) }}
          </span>
        </template>
        <template #cell-debt_limit="{ row }">
          {{ $format.currency(row.debt_limit || 0) }}
        </template>
        <template #cell-available_credit="{ row }">
          <span class="fw-semibold" :class="Number(row.available_credit || 0) <= 0 ? 'text-warning' : 'text-success'">
            {{ $format.currency(row.available_credit || 0) }}
          </span>
        </template>
        <template #cell-type="{ row }">
          <span class="badge" :class="row.is_system ? 'bg-dark' : 'bg-secondary'">
            {{ row.is_system ? 'Système' : 'Standard' }}
          </span>
        </template>
      </BootstrapDataTable>

      <!-- Dialog création / édition -->
      <PrimevueDialog v-model:visible="formDialogVisible" :modal="true" :style="{ width: '600px' }" :closable="false">
        <template #header>
          <h5 class="mb-0">
            {{ formDialogMode === 'create' ? 'Nouveau client' : 'Modifier le client' }}
          </h5>
        </template>

        <ClientForm :mode="formDialogMode" :client="selectedClient" :submitting="isSavingForm" @save="handleSave" @cancel="closeFormDialog" />
      </PrimevueDialog>
    </template>
  </component>
</template>

<script>
import ClientForm from './ClientForm.vue';
import { canBypassPermissions } from '@/support/permissions';
import {
  cleanFilterParams,
  countActiveFilters,
  extractApiTotal,
  extractApiList,
  loadModuleFilterState,
  saveModuleFilterValues,
  saveModuleFilterVisibility,
} from '@/support/moduleFilters';

const FILTER_MODULE = 'clients';
const DEFAULT_FILTERS = {
  search: '',
  type: '',
};

export default {
  name: 'ClientsView',
  components: {
    ClientForm,
  },
  data() {
    return {
      clients: [],
      clientsResponse: null,
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      table: {
        page: 1,
        perPage: 10,
        sortBy: 'name',
        sortDir: 'asc',
      },
      tableColumns: [
        { key: 'name', label: 'Nom', sortable: true },
        { key: 'phone', label: 'Téléphone' },
        { key: 'whatsapp_phone', label: 'WhatsApp' },
        { key: 'city', label: 'Ville' },
        { key: 'type', label: 'Type', class: 'text-center' },
        { key: 'current_debt', label: 'Dette', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'debt_limit', label: 'Plafond', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'available_credit', label: 'Crédit disponible', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'total_sales_amount', label: 'Montant ventes', sortable: true, class: 'text-end' },
      ],
      filters: { ...DEFAULT_FILTERS },
      filtersVisible: false,

      formDialogVisible: false,
      formDialogMode: 'create',
      selectedClient: null,
      isSavingForm: false,
      typeFilterOptions: [
        { label: 'Tous', value: '' },
        { label: 'Clients standards', value: 'standard' },
        { label: 'Clients système', value: 'system' },
      ],
    };
  },
  mounted() {
    this.restoreFilters();
    this.fetchClients();
  },
  computed: {
    totalRows() {
      return extractApiTotal(this.clientsResponse, this.clients.length);
    },
    activeFilterCount() {
      return countActiveFilters(this.filters, DEFAULT_FILTERS, ['search']);
    },
    currentUser() {
      return this.$store.getters['auth/currentUser'];
    },

    isCashier() {
      return this.currentUser?.role === 'caissier';
    },

    canManageClients() {
      return canBypassPermissions(this.currentUser);
    },

    clientsPageDescription() {
      if (this.canManageClients) {
        return 'Gestion des clients et consultation de leur historique de ventes.';
      }

      return 'Consultation des clients et de leur historique de ventes.';
    },

    layoutComponent() {
      return this.isCashier ? 'CashierLayout' : 'AdminLayout';
    },
  },
  watch: {
    filters: {
      deep: true,
      handler(filters) {
        saveModuleFilterValues(FILTER_MODULE, filters);
      },
    },
    filtersVisible(value) {
      saveModuleFilterVisibility(FILTER_MODULE, value);
    },
  },
  methods: {
    restoreFilters() {
      const state = loadModuleFilterState(FILTER_MODULE, DEFAULT_FILTERS);
      this.filters = state.values;
      this.filtersVisible = state.visible;
    },

    toggleFilters() {
      this.filtersVisible = !this.filtersVisible;
    },

    resetFilters() {
      this.filters = { ...DEFAULT_FILTERS };
      this.table.page = 1;
      this.fetchClients();
    },

    async fetchClients() {
      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const res = await this.$api.get('/clients', {
          params: {
            ...cleanFilterParams(this.filters),
            paginate: 1,
            page: this.table.page,
            per_page: this.table.perPage,
            sort_by: this.table.sortBy,
            sort_dir: this.table.sortDir,
          },
        });
        this.clientsResponse = res.data;
        this.clients = extractApiList(res.data);
      } catch (e) {
        console.error('Erreur chargement clients', e);
        this.errorMessage = 'Impossible de charger les clients.';
      } finally {
        this.isLoading = false;
      }
    },

    openCreateDialog() {
      if (!this.canManageClients) return;

      this.formDialogMode = 'create';
      this.selectedClient = null;
      this.formDialogVisible = true;
    },

    openEditDialog(row) {
      if (!this.canManageClients) return;

      this.formDialogMode = 'edit';
      this.selectedClient = row;
      this.formDialogVisible = true;
    },

    closeFormDialog() {
      this.formDialogVisible = false;
      this.selectedClient = null;
    },

    async handleSave(payload) {
      if (!this.canManageClients) return;
      if (this.isSavingForm) return;

      this.errorMessage = '';
      this.successMessage = '';
      this.isSavingForm = true;

      try {
        if (this.formDialogMode === 'create') {
          await this.$api.post('/clients', payload);
          this.successMessage = 'Client créé avec succès.';
        } else {
          const id = payload.id || this.selectedClient?.id;
          await this.$api.put(`/clients/${id}`, payload);
          this.successMessage = 'Client mis à jour avec succès.';
        }

        this.formDialogVisible = false;
        await this.fetchClients();
      } catch (error) {
        console.error('Erreur enregistrement client', error);

        let msg = 'Une erreur est survenue lors de la sauvegarde du client.';
        if (error && error.response && error.response.data && error.response.data.errors) {
          const rawErrors = Object.values(error.response.data.errors);
          const flatMessages = rawErrors.flat();
          msg = flatMessages.join(' ');
        }

        this.errorMessage = msg;
      } finally {
        this.isSavingForm = false;
      }
    },

    async deleteClient(row) {
      if (!this.canManageClients) return;

      const ok = await this.$confirmAction(`Supprimer le client "${row.name}" ?`);
      if (!ok) return;

      this.errorMessage = '';
      this.successMessage = '';

      try {
        await this.$api.delete(`/clients/${row.id}`);
        this.successMessage = 'Client supprimé.';
        await this.fetchClients();
      } catch (error) {
        console.error('Erreur suppression client', error);

        let msg = 'Une erreur est survenue lors de la suppression du client.';
        if (error && error.response && error.response.data && error.response.data.errors) {
          const rawErrors = Object.values(error.response.data.errors);
          const flatMessages = rawErrors.flat();
          msg = flatMessages.join(' ');
        }

        this.errorMessage = msg;
      }
    },

    goToShow(row) {
      this.$router.push({ name: 'ClientShow', params: { id: row.id } });
    },

    handleSortChange({ sortBy, sortDir }) {
      this.table.sortBy = sortBy;
      this.table.sortDir = sortDir;
      this.table.page = 1;
      this.fetchClients();
    },

    handlePageChange(page) {
      this.table.page = page;
      this.fetchClients();
    },

    handlePerPageChange(perPage) {
      this.table.perPage = perPage;
      this.table.page = 1;
      this.fetchClients();
    },

    handleTableSearch(search) {
      this.filters.search = search;
      this.table.page = 1;
      this.fetchClients();
    },
  },
};
</script>
